const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^[+]?[\d\s().-]{7,20}$/;
const PREVIEW_PARAMS = ['preview_theme_id', '_ab', '_fd', '_sc', 'view', 'pb'];

/**
 * Contact form with field validation and Shopify hCaptcha.
 * When storefront CAPTCHA is enabled, submits natively after Shopify.captcha.protect
 * so the h-captcha-response token is included (AJAX POSTs without a token get 400).
 */
class PjContactForm {
  /**
   * @param {HTMLFormElement} form
   */
  constructor(form) {
    this.form = form;
    this.submitButton = form.querySelector('[type="submit"]');
    this.defaultSubmitLabel = this.submitButton?.textContent?.trim() || '';
    this.i18n = this.#readI18n();
    this.errorStatus = form.querySelector('[data-pj-contact-error-status]');
    this.successStatus = form.querySelector('[data-pj-contact-success-status]');
    this.fields = [...form.querySelectorAll('[data-pj-contact-field]')];

    form.noValidate = true;
    form.dataset.pjContactReady = 'true';
    form.addEventListener('pj-contact-submit', () => this.#onSubmit());
    form.addEventListener('input', (event) => this.#onInput(event, false));
    form.addEventListener('blur', (event) => this.#onInput(event, true), true);
  }

  #readI18n() {
    const source = this.form.querySelector('[data-pj-contact-i18n]');
    try {
      return JSON.parse(source?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /**
   * @param {Event} event
   * @param {boolean} showEmpty
   */
  #onInput(event, showEmpty) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) return;
    const field = target.closest('[data-pj-contact-field]');
    if (field) this.#validateField(field, showEmpty);
  }

  async #onSubmit() {
    this.#syncFullName();
    this.#hideStatus(this.errorStatus);
    this.#hideStatus(this.successStatus);

    if (!this.#validateAll()) {
      const firstInvalid = this.form.querySelector('[aria-invalid="true"]');
      firstInvalid?.focus();
      return;
    }

    this.#setSubmitting(true);

    try {
      await this.#protectWithCaptcha();

      // With CAPTCHA enabled, protect() only wires the widget — the token is attached
      // on native submit. AJAX without a token returns 400 "Missing CAPTCHA token".
      if (this.#captchaEnabled() && !this.#captchaToken()) {
        this.#nativeSubmit();
        return;
      }

      const result = await this.#postForm();
      if (result === 'challenge') return;
      if (result === 'captcha') {
        this.#nativeSubmit();
        return;
      }
      if (result) {
        this.#showSuccess();
        this.form.reset();
        this.#syncFullName();
        this.fields.forEach((field) => this.#clearFieldError(field));
        this.successStatus?.focus();
      } else {
        this.#showFormError(this.i18n.error_submit);
      }
    } catch {
      this.#showFormError(this.i18n.error_submit);
    } finally {
      this.#setSubmitting(false);
    }
  }

  #syncFullName() {
    const first = this.form.querySelector('[name="contact[First name]"]');
    const last = this.form.querySelector('[name="contact[Last name]"]');
    const full = this.form.querySelector('[name="contact[name]"]');
    if (!(full instanceof HTMLInputElement)) return;
    const firstValue = first instanceof HTMLInputElement ? first.value.trim() : '';
    const lastValue = last instanceof HTMLInputElement ? last.value.trim() : '';
    full.value = [firstValue, lastValue].filter(Boolean).join(' ');
  }

  #captchaEnabled() {
    return typeof window.Shopify?.captcha?.protect === 'function';
  }

  #captchaToken() {
    const field = this.form.querySelector('[name="h-captcha-response"]');
    return field instanceof HTMLTextAreaElement || field instanceof HTMLInputElement
      ? field.value.trim()
      : '';
  }

  #protectWithCaptcha() {
    const captcha = window.Shopify?.captcha;
    if (!captcha || typeof captcha.protect !== 'function') return Promise.resolve();

    return new Promise((resolve) => {
      let settled = false;
      const done = () => {
        if (settled) return;
        settled = true;
        resolve();
      };
      captcha.protect(this.form, done);
      // Wire-up should be fast; do not POST without a token if this fires first.
      setTimeout(done, 4000);
    });
  }

  /**
   * Keep theme-preview query params so the POST response stays on this theme.
   * @returns {string}
   */
  #resolveActionUrl() {
    const actionUrl = new URL(this.form.action || window.location.href, window.location.origin);
    actionUrl.hash = '';
    const current = new URL(window.location.href);

    PREVIEW_PARAMS.forEach((key) => {
      const value = current.searchParams.get(key);
      if (value && !actionUrl.searchParams.has(key)) {
        actionUrl.searchParams.set(key, value);
      }
    });

    return actionUrl.toString();
  }

  async #postForm() {
    const response = await fetch(this.#resolveActionUrl(), {
      method: 'POST',
      body: new URLSearchParams(new FormData(this.form)),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
    });

    if (response.url.includes('/challenge')) {
      this.#nativeSubmit();
      return 'challenge';
    }

    if (response.status === 400) {
      let payload = null;
      try {
        payload = await response.clone().json();
      } catch {
        payload = null;
      }
      const errors = payload?.errors;
      const message = typeof errors === 'string' ? errors : errors?.captcha || errors?.form;
      if (message && /captcha/i.test(String(message))) return 'captcha';
      if (typeof message === 'string' && message.trim()) {
        this.#showFormError(message.trim());
        return false;
      }
      return 'captcha';
    }

    let posted = false;
    try {
      posted = new URL(response.url).searchParams.get('contact_posted') === 'true';
    } catch {
      posted = false;
    }

    // JSON success (Accept: application/json) — empty/ok body with redirect params, or HTML fallback.
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
      try {
        const data = await response.clone().json();
        if (data?.errors) {
          const message =
            typeof data.errors === 'string'
              ? data.errors
              : Object.values(data.errors).flat().filter(Boolean).join(' ');
          if (message && /captcha/i.test(message)) return 'captcha';
          if (message) {
            this.#showFormError(message);
            return false;
          }
        }
      } catch {
        // Fall through to HTML parsing.
      }
    }

    const html = await response.text();
    if (html.includes('contact_posted=true')) posted = true;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    if (doc.querySelector('.pj-contact-form__status--success:not([hidden])')) posted = true;
    if (doc.body?.querySelector('form[action*="/challenge"], #challenge-form')) {
      this.#nativeSubmit();
      return 'challenge';
    }

    const remoteError = doc.querySelector('.pj-contact-form__status--error:not([hidden]) p');
    if (!posted && remoteError?.textContent?.trim()) {
      this.#showFormError(remoteError.textContent.trim());
      return false;
    }

    return posted;
  }

  /**
   * Full navigation submit via requestSubmit so the submit event still fires and
   * Shopify/hCaptcha can attach h-captcha-response (form.submit() skips that).
   */
  #nativeSubmit() {
    this.form.dataset.pjContactReady = 'false';
    // requestSubmit() throws if the submitter is disabled.
    if (this.submitButton) this.submitButton.disabled = false;
    if (typeof this.form.requestSubmit === 'function') {
      this.form.requestSubmit();
      return;
    }
    this.form.submit();
  }

  #validateAll() {
    return this.fields.map((field) => this.#validateField(field, true)).every(Boolean);
  }

  /**
   * @param {Element} field
   * @param {boolean} showEmpty
   */
  #validateField(field, showEmpty) {
    const input = field.querySelector('input, textarea');
    if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLTextAreaElement)) return true;
    if (input.type === 'hidden') return true;

    const value = input.value.trim();
    const required = input.hasAttribute('required') || input.getAttribute('aria-required') === 'true';
    let message = '';

    if (required && !value) {
      message = showEmpty ? this.i18n.error_required : '';
    } else if (value && input.type === 'email' && !EMAIL_PATTERN.test(value)) {
      message = this.i18n.error_email;
    } else if (value && input.type === 'tel' && !PHONE_PATTERN.test(value)) {
      message = this.i18n.error_phone;
    } else if (value && input.name === 'contact[body]' && value.length < 4) {
      message = this.i18n.error_message;
    }

    if (message) {
      this.#setFieldError(field, input, message);
      return false;
    }

    this.#clearFieldError(field, input);
    return true;
  }

  /**
   * @param {Element} field
   * @param {HTMLInputElement | HTMLTextAreaElement} input
   * @param {string} message
   */
  #setFieldError(field, input, message) {
    field.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    const error = field.querySelector('[data-pj-contact-field-error]');
    if (error) {
      error.hidden = false;
      error.textContent = message;
    }
  }

  /**
   * @param {Element} field
   * @param {HTMLInputElement | HTMLTextAreaElement} [input]
   */
  #clearFieldError(field, input = field.querySelector('input, textarea')) {
    field.classList.remove('is-invalid');
    if (input instanceof HTMLElement) input.removeAttribute('aria-invalid');
    const error = field.querySelector('[data-pj-contact-field-error]');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }
  }

  /**
   * @param {string} message
   */
  #showFormError(message) {
    if (!this.errorStatus) return;
    const copy = this.errorStatus.querySelector('p');
    if (copy) copy.textContent = message;
    this.errorStatus.hidden = false;
    this.errorStatus.focus();
  }

  #showSuccess() {
    if (!this.successStatus) return;
    this.successStatus.hidden = false;
  }

  /**
   * @param {HTMLElement | null} status
   */
  #hideStatus(status) {
    if (status) status.hidden = true;
  }

  /**
   * @param {boolean} submitting
   */
  #setSubmitting(submitting) {
    if (!this.submitButton) return;
    this.submitButton.disabled = submitting;
    this.submitButton.textContent = submitting ? this.i18n.submitting || this.defaultSubmitLabel : this.defaultSubmitLabel;
  }
}

document.querySelectorAll('form.pj-contact-form').forEach((form) => {
  if (form instanceof HTMLFormElement) new PjContactForm(form);
});

function equalizeLocationCards() {
  const cards = [...document.querySelectorAll('.pj-contact__location')];
  if (cards.length < 2) return;
  cards.forEach((card) => {
    card.style.minHeight = '';
  });
  const tallest = Math.max(...cards.map((card) => card.offsetHeight));
  cards.forEach((card) => {
    card.style.minHeight = `${tallest}px`;
  });
}

equalizeLocationCards();
window.addEventListener('resize', equalizeLocationCards);
